import { useId } from 'react';

import { useTranslation } from 'react-i18next';

import { FileItemRaw } from '@entities/attachment';
import { CircularLoader, Icon, TextArea, UiButton, UiText } from '@shared/ui';

import cls from './TaskComments.module.css';
import { TASK_COMMENT_MAX_TEXT_LENGTH } from '../model/config';
import { useTaskCommentInputSize } from '../model/useTaskCommentInputSize';

import type { TTaskCommentFieldErrors } from '../model/types';

interface TaskCommentsFooterProps {
    containerRef: React.RefObject<HTMLElement>;
    text: string;
    files: File[];
    isInvalid: boolean;
    isSubmitting: boolean;
    onSubmit: () => Promise<void>;
    onTextChange: (text: string) => void;
    onAttachFiles: () => void;
    onRemoveFile: (file: File) => void;
    isPending: boolean;
    errorMessage?: string;
    fieldErrors?: TTaskCommentFieldErrors;
}

export const TaskCommentsFooter = ({
    containerRef,
    onSubmit,
    text,
    files,
    isInvalid,
    isSubmitting,
    onTextChange,
    onAttachFiles,
    onRemoveFile,
    isPending,
    errorMessage,
    fieldErrors = {},
}: TaskCommentsFooterProps) => {
    const { t } = useTranslation();
    const textErrorId = useId();
    const { formRef, textareaRef, maxHeight } = useTaskCommentInputSize(containerRef);
    const isSendDisabled = isPending || (!text.trim() && files.length === 0);
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void onSubmit();
    };

    return (
        <form ref={formRef} className={cls.Footer} onSubmit={handleSubmit}>
            {errorMessage && (
                <UiText variant="mediumPlain" role="alert">
                    {errorMessage}
                </UiText>
            )}
            {files.length > 0 && (
                <ul className={cls.Attachments}>
                    {files.map((file, index) => (
                        <FileItemRaw
                            key={`${file.name}-${file.lastModified}-${index}`}
                            file={file}
                            fileItemClassName={cls.AttachmentItem}
                            isLoading={false}
                            onCancel={isPending ? undefined : onRemoveFile}
                        />
                    ))}
                </ul>
            )}
            {fieldErrors.files && (
                <UiText variant="mediumPlain" className={cls.InputError} role="alert">
                    {fieldErrors.files}
                </UiText>
            )}
            <div className={cls.FooterRow}>
                <div className={cls.AttachButtonWrapper}>
                    <UiButton
                        className={cls.AttachButton}
                        variant="icon"
                        type="button"
                        disabled={isPending}
                        title={t('Comments.Footer.AttachBtn.title')}
                        startIcon={<Icon iconName="paperClip" width={12} height={12} />}
                        onClick={onAttachFiles}
                    />
                </div>

                <div className={cls.InputGroup}>
                    <TextArea
                        inputName="task-comment"
                        classes={cls.Input}
                        textareaClassName={cls.TextArea}
                        innerRef={textareaRef}
                        value={text}
                        onChange={onTextChange}
                        maxLength={TASK_COMMENT_MAX_TEXT_LENGTH}
                        isDisabled={isPending}
                        minRows={1}
                        maxHeight={maxHeight}
                        autoResize
                        isError={isInvalid || Boolean(fieldErrors.text)}
                        aria-describedby={fieldErrors.text ? textErrorId : undefined}
                    />
                    {fieldErrors.text && (
                        <UiText
                            id={textErrorId}
                            variant="mediumPlain"
                            className={cls.InputError}
                            role="alert"
                        >
                            {fieldErrors.text}
                        </UiText>
                    )}
                </div>
                <div className={cls.SendActionSlot}>
                    {isSubmitting ? (
                        <CircularLoader size={20} thickness={2} />
                    ) : (
                        <UiButton
                            className={cls.sendButton}
                            variant="icon"
                            type="submit"
                            disabled={isSendDisabled}
                            title={t('Comments.Footer.SendMessageBtn.title')}
                            startIcon={
                                <Icon
                                    width={24}
                                    height={24}
                                    iconName="sendNotesDeal"
                                    fill="currentColor"
                                />
                            }
                        />
                    )}
                </div>
            </div>
        </form>
    );
};
